import { z } from 'zod'
import { MailProviderId } from './mail.js'
import { SCHEMA_VERSION } from './version.js'

/**
 * Versioned email provider configuration — PRD §12.7, §§11.6, 15.8.
 *
 * `EmailProvider` owns provider configuration, sending, outcome reconciliation and
 * verified event normalization only. Configuration is versioned so an attempt can pin
 * the exact version it was dispatched with, and each environment has **one Active
 * sending provider**: no automatic failover, no parallel sender (§15.8,
 * V17-CFG-05). A second sender exists only as an inactive seam.
 */

const IsoDateTime = z.string().datetime()

export const MailEnvironment = z.enum(['dev', 'test', 'prod'])
export type MailEnvironment = z.infer<typeof MailEnvironment>

/** Seam state. Exactly one configuration per environment is `LIVE`. */
export const ProviderCutoverState = z.enum(['LIVE', 'SEAM_ONLY', 'RETIRED'])
export type ProviderCutoverState = z.infer<typeof ProviderCutoverState>

export const ProviderCapability = z.enum([
  'send_email',
  'delivery_events',
  'bounce_events',
  'complaint_events',
  'suppression_management',
  'dkim_signing',
])
export type ProviderCapability = z.infer<typeof ProviderCapability>

export const ProviderEventSource = z.enum(['queue', 'webhook', 'inbound_routing'])
export type ProviderEventSource = z.infer<typeof ProviderEventSource>

/** Approved sending identity; the literal address is a reference, never PII in logs. */
export const ApprovedSendingIdentity = z
  .object({
    identity_ref: z.string().min(1).max(128),
    domain: z.string().min(1).max(255),
    verified: z.boolean(),
  })
  .strict()
export type ApprovedSendingIdentity = z.infer<typeof ApprovedSendingIdentity>

/**
 * Account quotas are *verified* facts, not assumptions: an unverified quota is
 * `null` and callers fail closed (V17-CFG-06). No quota value is invented here.
 */
export const VerifiedProviderQuotas = z
  .object({
    daily_messages: z.number().int().positive().nullable(),
    max_send_rate_per_second: z.number().positive().nullable(),
    verified_at: IsoDateTime.optional(),
  })
  .strict()
export type VerifiedProviderQuotas = z.infer<typeof VerifiedProviderQuotas>

export const ProviderConfiguration = z
  .object({
    provider: MailProviderId,
    config_version: z.number().int().positive(),
    environment: MailEnvironment,
    cutover_state: ProviderCutoverState,
    approved_sending_identities: z.array(ApprovedSendingIdentity).min(1),
    /** Secret-store references only — never secret material. */
    secret_refs: z.array(z.string().min(1).max(255)).min(1),
    declared_capabilities: z.array(ProviderCapability).min(1),
    verified_quotas: VerifiedProviderQuotas,
    bounce_event_source: ProviderEventSource,
  })
  .strict()
export type ProviderConfiguration = z.infer<typeof ProviderConfiguration>

/**
 * The provider selection for one environment.
 *
 * The "one active sending provider per environment" rule (§12.7, §15.8) is encoded in
 * the shape: exactly one configuration is `LIVE`, every configuration belongs to the
 * set's environment, and versions are unique.
 */
export const ProviderConfigurationSet = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    environment: MailEnvironment,
    configurations: z.array(ProviderConfiguration).min(1),
  })
  .strict()
  .superRefine((set, ctx) => {
    const live = set.configurations.filter((config) => config.cutover_state === 'LIVE')
    if (live.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'exactly one active sending provider per environment (§15.8)',
        path: ['configurations'],
      })
    }
    const versions = set.configurations.map((config) => config.config_version)
    if (new Set(versions).size !== versions.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'provider configuration versions must be unique',
        path: ['configurations'],
      })
    }
    for (const [index, config] of set.configurations.entries()) {
      if (config.environment !== set.environment) {
        ctx.addIssue({
          code: 'custom',
          message: 'configuration environment must match its set',
          path: ['configurations', index, 'environment'],
        })
      }
    }
  })
export type ProviderConfigurationSet = z.infer<typeof ProviderConfigurationSet>
